import { Component, OnInit, ViewChild } from '@angular/core';
import { UtilityService } from '@services/utility.service';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import * as Highcharts from 'highcharts';
import { DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { StationDevicesDropDown } from '@/Interface/analysisEmi';

@Component({
  selector: 'app-wapda-graph',
  templateUrl: './wapda-graph.component.html',
  styleUrls: ['./wapda-graph.component.scss']
})
export class WapdaGraphComponent implements OnInit {
  // NE=50922688
  stationCode: string = ''
  loader: boolean = false;
  selectedtype17Device: StationDevicesDropDown | null = null;
  selectedtype47Device: StationDevicesDropDown | null = null;
  type17DevicesList: StationDevicesDropDown[] = [];
  type47DevicesList: StationDevicesDropDown[] = [];
  plant1Data: any = null;

  // multi line pv chat variables 
  Highcharts: typeof Highcharts = Highcharts;
  pvVoltages: string[] = [];
  pvCurrents: string[] = [];
  pvEnergy: string[] = [];
  pvPowers: string[] = [];
  pvTemperature: string[] = [];
  pvPercentage: string[] = [];
  pvFrequency: string[] = [];

  // new pv graph
  selectedVoltages: string[] = [];
  selectedCurrents: string[] = [];
  selectedEnergy: string[] = [];
  selectedPowers: string[] = [];
  selectedTemperature: string[] = [];
  selectedPercentage: string[] = [];
  selectedFrequency: string[] = [];

  private colorObj: { [key: string]: string } = {
    current: '#FFBF00',
    frequency: '#02D3C8',
    power: '#414141',
    temperature: '#FF7800',
    voltage: '#EF4323',
    energy: '#23B762',
    percentage: '#0E9CFF',
  };

  public selectedPVDate: Date = new Date();
  datePickerConfig: Partial<BsDaterangepickerConfig> = {
    showWeekNumbers: false,
    adaptivePosition: true,
  };

  public pvMultilineChartOptions!: Highcharts.Options;
  @ViewChild('pvMultiChart') pvMultiChart: any;
  updateFlag = false;
  public lineChartLabels: Array<any> = [];
  pvGraphRes: any[];
  pvGraphKey: any[];
  pvGraphResRaw: any;

  private previousSelections = {
    voltages: [],
    currents: [],
    powers: [],
    energy: [],
    percentage: [],
    temperature: [],
    frequency: []
  };

  constructor(
    private _utilityService: UtilityService,
    private datePipe: DatePipe,
    private toaster: ToastrService,
  ) {
    this._utilityService.selectedProduct$.subscribe((value) => {
      this.stationCode = value;
    });
  }

  ngOnInit(): void {
    this.gettype17DevicesList(this.stationCode);
    this.gettype47DevicesList(this.stationCode);
  }

  processGraphTime(arrTime: any) {
    this.lineChartLabels.length = 0;
    arrTime.forEach((e) => {
      const date = new Date(Number(e) * 1000);
      const time = this.datePipe.transform(date, 'hh:mm a');
      this.lineChartLabels.push(time);
    });
  }

  updateChartCategories() {
    const xAxis = this.pvMultilineChartOptions?.xAxis;
    if (Array.isArray(xAxis)) {
      (xAxis[0] as any).categories = [...this.lineChartLabels];
    } else {
      console.warn('Chart options not initialized. Skipping category update.');
    }
  }

  getSelectedYAxisCount(): number {
    let count = 0;
    if (this.selectedVoltages && this.selectedVoltages.length) count++;
    if (this.selectedCurrents && this.selectedCurrents.length) count++;
    if (this.selectedPowers && this.selectedPowers.length) count++;
    if (this.selectedEnergy && this.selectedEnergy.length) count++;
    if (this.selectedPercentage && this.selectedPercentage.length) count++;
    if (this.selectedTemperature && this.selectedTemperature.length) count++;
    if (this.selectedFrequency && this.selectedFrequency.length) count++;
    return count;
  }

  onDropdownSelectionChange() {
    const yAxisTypeCount = this.getSelectedYAxisCount();

    if (yAxisTypeCount > 3) {
      this.toaster.warning('You can select a maximum of 3 filter types for Y-axis.');

      // Revert to previous selections
      this.selectedVoltages = [...this.previousSelections.voltages];
      this.selectedCurrents = [...this.previousSelections.currents];
      this.selectedPowers = [...this.previousSelections.powers];
      this.selectedEnergy = [...this.previousSelections.energy];
      this.selectedPercentage = [...this.previousSelections.percentage];
      this.selectedTemperature = [...this.previousSelections.temperature];
      this.selectedFrequency = [...this.previousSelections.frequency];
      return;
    }

    // Store current selections as valid
    this.previousSelections = {
      voltages: [...this.selectedVoltages],
      currents: [...this.selectedCurrents],
      powers: [...this.selectedPowers],
      energy: [...this.selectedEnergy],
      percentage: [...this.selectedPercentage],
      temperature: [...this.selectedTemperature],
      frequency: [...this.selectedFrequency]
    };

    this.performChartUpdate();
  }

  performChartUpdate() {
    setTimeout(() => {
      this.updateChartSeries();
    }, 0);
  }

  clearChartSeries() {
    if (!this.pvMultilineChartOptions) {
      console.warn('Chart options not initialized yet');
      return;
    }

    this.pvMultilineChartOptions.series = [];

    if (Array.isArray(this.pvMultilineChartOptions.xAxis)) {
      (this.pvMultilineChartOptions.xAxis[0] as any).categories = [];
    }

    this.updateChart();
  }

  clearAllDropdownSelections() {
    this.selectedVoltages = [];
    this.selectedCurrents = [];
    this.selectedPowers = [];
    this.selectedEnergy = [];
    this.selectedPercentage = [];
    this.selectedTemperature = [];
    this.selectedFrequency = [];

    this.previousSelections = {
      voltages: [],
      currents: [],
      powers: [],
      energy: [],
      percentage: [],
      temperature: [],
      frequency: []
    };
  }

  clearPVChart() {
    this.clearChartSeries()
    this.clearAllDropdownSelections()
  }

  generateSeriesColor(type: string, source: string, index: number): string {
    const baseColor = this.colorObj[type];

    // Convert hex to RGB
    const r = parseInt(baseColor.slice(1, 3), 16);
    const g = parseInt(baseColor.slice(3, 5), 16);
    const b = parseInt(baseColor.slice(5, 7), 16);

    if (source === 'power') {
      // Power sensor: Lighter and more saturated
      const lightenFactor = 0.5;
      const newR = Math.min(255, Math.round(r + (255 - r) * lightenFactor));
      const newG = Math.min(255, Math.round(g + (255 - g) * lightenFactor));
      const newB = Math.min(255, Math.round(b + (255 - b) * lightenFactor));

      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    } else {
      // Grid meter: Darker
      const darkenFactor = 0.7; // Keep 70% of original brightness
      const newR = Math.round(r * darkenFactor);
      const newG = Math.round(g * darkenFactor);
      const newB = Math.round(b * darkenFactor);

      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
  }

  formatSeriesName(key: string): string {
    // Convert "ab_u" to "AB U" or "PV1Voltage" to "PV1 Voltage"
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.toUpperCase())
      .join(' ');
  }

  capitalize(text: any): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  formatSmartValue(value: number, unit: string): string {
    if (unit === 'W') {
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}GW`;
      if (value >= 1_000) return `${(value / 1_000).toFixed(1)}MW`;
      return `${value}KW`;
    }

    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}G${unit}`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M${unit}`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K${unit}`;
    return `${value}${unit}`;
  }

  createDynamicYAxes(): Highcharts.YAxisOptions[] {
    const axes: Highcharts.YAxisOptions[] = [];
    let axisCount = 0;

    const hasData = (data?: (number | null)[]) =>
      data?.some(v => v != null) ?? false;

    const formatWithUnit = (value: number, unit: string): string => {
      if (unit === 'W') {
        if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}GW`;
        if (value >= 1_000) return `${(value / 1_000).toFixed(1)}MW`;
        return `${value}KW`;
      }

      if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}G${unit}`;
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M${unit}`;
      if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K${unit}`;
      return `${value}${unit}`;
    };

    const addAxis = (keys: string[], type: keyof typeof this.colorObj, unit: string) => {
      if (axisCount >= 3) return;

      const fieldSourceMap = this.pvGraphRes?.[0]?.field_source_map || {};
      const containsData = keys.some(key => {
        const sources = fieldSourceMap[key] || [];
        return sources.some(source => {
          const dataKey = `${key}__${source}`;
          return hasData(this.pvGraphRes?.[0]?.[dataKey]);
        });
      });

      if (containsData) {
        axes.push({
          gridLineWidth: axisCount === 0 ? 1 : 0,
          labels: {
            style: { color: this.colorObj[type] },
            formatter: function () {
              const val = this.value as number;
              return formatWithUnit(val, unit);
            }
          },
          title: {
            text: this.capitalize(type),
            style: {
              color: this.colorObj[type],
              fontWeight: 'bold'
            }
          },
          opposite: axisCount !== 0,
        });
        axisCount++;
      }
    };

    addAxis(this.selectedVoltages, 'voltage', 'V');
    addAxis(this.selectedCurrents, 'current', 'A');
    addAxis(this.selectedPowers, 'power', 'W');
    addAxis(this.selectedEnergy, 'energy', 'J');
    addAxis(this.selectedPercentage, 'percentage', '%');
    addAxis(this.selectedTemperature, 'temperature', '°C');
    addAxis(this.selectedFrequency, 'frequency', 'Hz');

    return axes;
  }

  updateChartSeries() {
    const newSeries: Highcharts.SeriesOptionsType[] = [];
    let seriesIndex = 0;

    const safeData = (arr?: (number | null)[]) =>
      (arr ?? []).map(v => (v === 0 || v == null ? null : v));

    const dynamicYAxes = this.createDynamicYAxes();

    const axisMap: Record<string, number> = {};
    let axisCounter = 0;

    const fieldSourceMap = this.pvGraphRes?.[0]?.field_source_map || {};

    const mapAxis = (keys: string[], type: string) => {
      const hasDataForType = keys.some(key => {
        const sources = fieldSourceMap[key] || [];
        return sources.some(source => {
          const dataKey = `${key}__${source}`;
          return this.pvGraphRes?.[0]?.[dataKey];
        });
      });

      if (hasDataForType) {
        axisMap[type] = axisCounter++;
      }
    };

    mapAxis(this.selectedVoltages, 'voltage');
    mapAxis(this.selectedCurrents, 'current');
    mapAxis(this.selectedPowers, 'power');
    mapAxis(this.selectedEnergy, 'energy');
    mapAxis(this.selectedPercentage, 'percentage');
    mapAxis(this.selectedTemperature, 'temperature');
    mapAxis(this.selectedFrequency, 'frequency');

    const formatTooltipValue = (value: number, unit: string): string => {
      if (unit === 'W') {
        if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} GW`;
        if (value >= 1_000) return `${(value / 1_000).toFixed(2)} MW`;
        return `${value.toFixed(2)} KW`;
      }

      if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)} G${unit}`;
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} M${unit}`;
      if (value >= 1_000) return `${(value / 1_000).toFixed(2)} K${unit}`;
      return `${value} ${unit}`;
    };

    const addSeries = (keys: string[], type: string, unit: string) => {
      if (axisMap[type] !== undefined) {
        keys.forEach(key => {
          const sources = fieldSourceMap[key] || [];

          sources.forEach(source => {
            const dataKey = `${key}__${source}`;
            if (this.pvGraphRes?.[0]?.[dataKey]) {
              const sourceLabel = source === 'grid' ? 'Grid Meter' : 'Power Sensor';
              const dashStyle = source === 'power' ? 'Dash' : 'Solid';

              newSeries.push({
                name: `${sourceLabel} - ${this.formatSeriesName(key)}`,
                type: 'line',
                yAxis: axisMap[type],
                data: safeData(this.pvGraphRes[0][dataKey]),
                color: this.generateSeriesColor(type, source, seriesIndex), // Pass source here
                dashStyle: dashStyle,
                tooltip: {
                  pointFormatter: function () {
                    const formattedValue = formatTooltipValue(this.y || 0, unit);
                    return `<span style="color:${this.color}">●</span> <b>${this.series.name}</b>: ${formattedValue}<br/>`;
                  }
                },
                turboThreshold: 0,
                lineWidth: 2, // Make lines slightly thicker for better visibility
              });
              seriesIndex++;
            }
          });
        });
      }
    };

    addSeries(this.selectedVoltages, 'voltage', 'V');
    addSeries(this.selectedCurrents, 'current', 'A');
    addSeries(this.selectedPowers, 'power', 'W');
    addSeries(this.selectedEnergy, 'energy', 'J');
    addSeries(this.selectedPercentage, 'percentage', '%');
    addSeries(this.selectedTemperature, 'temperature', '°C');
    addSeries(this.selectedFrequency, 'frequency', 'Hz');

    this.pvMultilineChartOptions = {
      chart: {
        zooming: { type: 'xy' },
        type: 'line',
        backgroundColor: 'transparent',
        height: 400,
        width: null,
        marginBottom: 80,
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      xAxis: {
        type: 'category',
        categories: [...this.lineChartLabels],
        crosshair: true
      },
      yAxis: dynamicYAxes,
      tooltip: {
        shared: true
      },
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        y: 40,
        symbolRadius: 16,
        itemStyle: {
          textOverflow: 'ellipsis',
          width: 150
        },
      },
      plotOptions: {
        series: {
          connectNulls: true,
          marker: {
            enabled: false,
            symbol: 'circle'
          }
        }
      },
      series: newSeries,
      responsive: {
        rules: [{
          condition: {
            maxWidth: 500
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom'
            }
          }
        }]
      }
    };

    this.updateChart();
  }

  updateChart() {
    this.pvMultilineChartOptions = { ...this.pvMultilineChartOptions };
    this.updateFlag = !this.updateFlag;

    setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.pvMultiChart?.chart) {
            this.pvMultiChart.chart.reflow();
            const containerWidth = this.pvMultiChart.chart.container.offsetWidth;
            const containerHeight = this.pvMultiChart.chart.container.offsetHeight;
            this.pvMultiChart.chart.setSize(containerWidth, containerHeight, false);
            console.log('Reflowed width:', containerWidth);
          }
        });
      });
    }, 0);
  }

  SetGraphData() {
    if (!this.hasFiltersSelected()) {
      console.warn('No filters selected, skipping chart update.');
      this.pvMultilineChartOptions = null;
      return;
    }

    if (!this.pvGraphRes || !this.pvGraphRes[0]) {
      console.error('No graph data available');
      return;
    }

    this.updateChartSeries();
  }

  setGraphicalView(devId: number, dt: string | Date) {
    this.loader = true;
    this._utilityService.newPvGraphsList(devId, dt).subscribe((res) => {
      if (res) {
        this.pvGraphRes = res;
        this.pvGraphKey = this.pvGraphRes[0].keys;
        this.mapPvDropDowns(this.pvGraphRes[0].dropdown_keys || {});
        this.processGraphTime(this.pvGraphRes[0].time);
        this.updateChartCategories();
        this.SetGraphData();
        this.loader = false;
      }
    });
  }

  hasFiltersSelected(): boolean {
    return (
      (this.selectedVoltages?.length ?? 0) > 0 ||
      (this.selectedCurrents?.length ?? 0) > 0 ||
      (this.selectedPowers?.length ?? 0) > 0 ||
      (this.selectedEnergy?.length ?? 0) > 0 ||
      (this.selectedPercentage?.length ?? 0) > 0 ||
      (this.selectedTemperature?.length ?? 0) > 0 ||
      (this.selectedFrequency?.length ?? 0) > 0
    );
  }

  mapPvDropDowns(k: any) {
    this.pvVoltages = k.voltages || [];
    this.pvCurrents = k.currents || [];
    this.pvEnergy = k.energy || [];
    this.pvPowers = k.powers || [];
    this.pvTemperature = k.temperature || [];
    this.pvPercentage = k.percentage || [];
    this.pvFrequency = k.frequency || [];
  }

  fetchWapdaGenerationData(date: Date): void {
    if (!date) {
      this.toaster.warning('Date is required.');
      return;
    }

    if (!this.selectedtype17Device && !this.selectedtype47Device) {
      this.toaster.warning('Please select at least one device.');
      return;
    }

    this.loader = true;
    const formattedDate = this.datePipe.transform(date, 'yyyy-MM-dd');

    const gridDevId = this.selectedtype17Device?.devId ? String(this.selectedtype17Device.devId) : null;
    const powerDevId = this.selectedtype47Device?.devId ? String(this.selectedtype47Device.devId) : null;

    this._utilityService.getWapdaGraphicalView(gridDevId, powerDevId, formattedDate).subscribe({
      next: (res) => {
        if (res) {
          this.pvGraphRes = this.transformWapdaData(res);
          console.log('Transformed Wapda Graph Data:', this.pvGraphRes);

          if (this.pvGraphRes && this.pvGraphRes[0]) {
            this.pvGraphResRaw = res;
            this.pvGraphRes = this.transformWapdaData(res);
            this.pvGraphKey = this.pvGraphRes[0].keys;
            this.mapPvDropDowns(this.pvGraphRes[0].dropdown_keys || {});
            this.processGraphTime(this.pvGraphRes[0].time);
            this.filterSelections();
            this.updateChartCategories();
            this.SetGraphData();
            this.toaster.success('Data loaded successfully');
          } else {
            this.clearAllData();
            this.toaster.info('No data available for selected devices');
          }
        }
        this.loader = false;
      },
      error: (err) => {
        console.error('Error fetching data:', err);
        this.toaster.error('Failed to load data');
        this.loader = false;
      }
    });
  }

  onDateChange(newDate: Date): void {
    this.selectedPVDate = newDate;
    if (this.selectedtype17Device || this.selectedtype47Device) {
      this.fetchWapdaGenerationData(newDate);
    }
  }

  public selectType17Device(device: StationDevicesDropDown): void {
    this.selectedtype17Device = device;

    if (device && this.selectedPVDate) {
      this.fetchWapdaGenerationData(this.selectedPVDate);
    } else if (!device) {
      if (this.selectedtype47Device && this.selectedPVDate) {
        this.fetchWapdaGenerationData(this.selectedPVDate);
      } else {
        this.clearAllData();
      }
    }
  }

  public selectType47Device(device: StationDevicesDropDown): void {
    this.selectedtype47Device = device;

    if (device && this.selectedPVDate) {
      this.fetchWapdaGenerationData(this.selectedPVDate);
    } else if (!device) {
      if (this.selectedtype17Device && this.selectedPVDate) {
        this.fetchWapdaGenerationData(this.selectedPVDate);
      } else {
        this.clearAllData();
      }
    }
  }

  gettype17DevicesList(stationCode: any): void {
    let type = 17
    this.type17DevicesList = [];
    if (stationCode) {
      this._utilityService.getStationInverterData(stationCode, type).subscribe({
        next: (res) => {
          this.type17DevicesList = res || [];
        },
        error: () => {
          this.type17DevicesList = [];
        }
      });
    }
  }

  gettype47DevicesList(stationCode: any): void {
    this.type47DevicesList = [];
    let type = 47
    if (stationCode) {
      this._utilityService.getStationInverterData(stationCode, type).subscribe({
        next: (res) => {
          this.type47DevicesList = res || [];
        },
        error: () => {
          this.type47DevicesList = [];
        }
      });
    }
  }

  clearAllData(): void {
    this.pvGraphRes = null;
    this.pvVoltages = [];
    this.pvCurrents = [];
    this.pvEnergy = [];
    this.pvPowers = [];
    this.pvTemperature = [];
    this.pvPercentage = [];
    this.pvFrequency = [];
    this.lineChartLabels = [];
    this.clearAllDropdownSelections();
    this.pvMultilineChartOptions = null;
  }

  transformWapdaData(response: any): any {
    const gridMeter = response.grid_meter || [];
    const powerSensor = response.power_sensor || [];

    // Track which fields come from which source
    const gridFieldsSet = new Set<string>();
    const powerFieldsSet = new Set<string>();
    const allFieldsSet = new Set<string>();

    gridMeter.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'collectTime') {
          gridFieldsSet.add(key);
          allFieldsSet.add(key);
        }
      });
    });

    powerSensor.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'collectTime') {
          powerFieldsSet.add(key);
          allFieldsSet.add(key);
        }
      });
    });

    // Create mapping for fields that exist in both sources
    const fieldSourceMap: { [key: string]: string[] } = {};
    allFieldsSet.forEach(field => {
      const sources = [];
      if (gridFieldsSet.has(field)) sources.push('grid');
      if (powerFieldsSet.has(field)) sources.push('power');
      fieldSourceMap[field] = sources;
    });

    // Categorize fields by type (without prefixes)
    const voltages: string[] = [];
    const currents: string[] = [];
    const powers: string[] = [];
    const energy: string[] = [];
    const frequencies: string[] = [];
    const percentages: string[] = [];

    allFieldsSet.forEach(field => {
      const lowerField = field.toLowerCase();

      if (lowerField.includes('_u') || lowerField.includes('voltage')) {
        voltages.push(field);
      } else if (lowerField.includes('_i') || lowerField.includes('current')) {
        currents.push(field);
      } else if (lowerField.includes('power') && !lowerField.includes('factor')) {
        powers.push(field);
      } else if (lowerField.includes('cap') || lowerField.includes('energy')) {
        energy.push(field);
      } else if (lowerField.includes('frequency')) {
        frequencies.push(field);
      } else if (lowerField.includes('factor')) {
        percentages.push(field);
      }
    });

    // Create time array and data arrays for each field+source combination
    const timeArray: number[] = [];
    const dataArrays: { [key: string]: (number | null)[] } = {};

    // Get unique timestamps from both sources
    const allTimes = [
      ...gridMeter.map(item => item.collectTime),
      ...powerSensor.map(item => item.collectTime)
    ];
    const uniqueTimes = [...new Set(allTimes)].sort((a, b) => a - b);

    // Initialize data arrays for each field+source combination
    allFieldsSet.forEach(field => {
      const sources = fieldSourceMap[field];
      sources.forEach(source => {
        const key = `${field}__${source}`;
        dataArrays[key] = [];
      });
    });

    uniqueTimes.forEach(time => {
      timeArray.push(time / 1000); // Convert to seconds

      const gridData = gridMeter.find(item => item.collectTime === time);
      const powerData = powerSensor.find(item => item.collectTime === time);

      allFieldsSet.forEach(field => {
        const sources = fieldSourceMap[field];

        if (sources.includes('grid')) {
          const key = `${field}__grid`;
          dataArrays[key].push(gridData?.[field] ?? null);
        }

        if (sources.includes('power')) {
          const key = `${field}__power`;
          dataArrays[key].push(powerData?.[field] ?? null);
        }
      });
    });

    // Return in expected format with source mapping
    return [{
      time: timeArray,
      keys: Array.from(allFieldsSet),
      field_source_map: fieldSourceMap,
      dropdown_keys: {
        voltages: voltages.filter(v =>
          fieldSourceMap[v].some(source => dataArrays[`${v}__${source}`].some(val => val !== null))
        ),
        currents: currents.filter(v =>
          fieldSourceMap[v].some(source => dataArrays[`${v}__${source}`].some(val => val !== null))
        ),
        powers: powers.filter(v =>
          fieldSourceMap[v].some(source => dataArrays[`${v}__${source}`].some(val => val !== null))
        ),
        energy: energy.filter(v =>
          fieldSourceMap[v].some(source => dataArrays[`${v}__${source}`].some(val => val !== null))
        ),
        frequency: frequencies.filter(v =>
          fieldSourceMap[v].some(source => dataArrays[`${v}__${source}`].some(val => val !== null))
        ),
        percentage: percentages.filter(v =>
          fieldSourceMap[v].some(source => dataArrays[`${v}__${source}`].some(val => val !== null))
        ),
        temperature: []
      },
      ...dataArrays
    }];
  }

  filterSelections(): void {
    // Remove selected items that are no longer in dropdown options
    this.selectedVoltages = this.selectedVoltages.filter(v => this.pvVoltages.includes(v));
    this.selectedCurrents = this.selectedCurrents.filter(c => this.pvCurrents.includes(c));
    this.selectedPowers = this.selectedPowers.filter(p => this.pvPowers.includes(p));
    this.selectedEnergy = this.selectedEnergy.filter(e => this.pvEnergy.includes(e));
    this.selectedFrequency = this.selectedFrequency.filter(f => this.pvFrequency.includes(f));
    this.selectedPercentage = this.selectedPercentage.filter(p => this.pvPercentage.includes(p));
    this.selectedTemperature = this.selectedTemperature.filter(t => this.pvTemperature.includes(t));
  }
  getDataSourceLabelForKey(key: string): string {
    const gridKeys = new Set(Object.keys(this.pvGraphRes?.[0] || {}));
    const gridMeterKeys = new Set(Object.keys((this.pvGraphResRaw?.grid_meter?.[0]) || {}));
    const powerSensorKeys = new Set(Object.keys((this.pvGraphResRaw?.power_sensor?.[0]) || {}));

    if (powerSensorKeys.has(key)) return 'Power Sensor';
    if (gridMeterKeys.has(key)) return 'Grid';

    return 'Unknown';
  }
}
